import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import type { Control, UseFormRegister } from 'react-hook-form';
import { useWatch } from 'react-hook-form';

import { useTranslate } from '~/shared/context/use-translate';

import { CommentableTitle } from '../../comments';
import { SidepanelTitle } from '../components/SidepanelTitle';
import { TitleSkeleton } from '../components/SkeletonLoaders';
import type { ProfileForm } from './types';

type JobProfileTitleProps = {
    readonly activeLanguageIndex: number;
    readonly architectureId: string;
    readonly control: Control<ProfileForm>;
    readonly isCommentable: boolean;
    readonly isEditing: boolean;
    readonly isGeneratingTranslations: boolean;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly register: UseFormRegister<ProfileForm>;
    readonly selectedVersion: string | undefined;
    readonly onInput: () => void;
};

export const JobProfileTitle: FunctionComponent<JobProfileTitleProps> = ({
    activeLanguageIndex,
    architectureId,
    control,
    isCommentable,
    isEditing,
    isGeneratingTranslations,
    itemId,
    itemType,
    onInput,
    register,
    selectedVersion,
}) => {
    const translate = useTranslate();
    const watchedTitle = useWatch({ control, name: `translations.${activeLanguageIndex}.title` });
    const title = watchedTitle ?? '';

    if (isGeneratingTranslations && is.falsy(watchedTitle)) {
        return (
            <div className="mt-s">
                <TitleSkeleton />
            </div>
        );
    }

    const sidepanelTitle = (
        <SidepanelTitle
            key={activeLanguageIndex}
            architectureId={architectureId}
            className="mt-s"
            isEditing={isEditing}
            itemId={itemId}
            itemType={itemType}
            placeholder={translate('pages.job-architecture.enter-job-profile-title')}
            register={register(`translations.${activeLanguageIndex}.title`)}
            title={title}
            version={selectedVersion}
            onInput={onInput}
        />
    );

    return isCommentable ? (
        <CommentableTitle itemId={itemId} newThreadCreationDisabled={false} title={title}>
            {sidepanelTitle}
        </CommentableTitle>
    ) : (
        sidepanelTitle
    );
};
